import { UserType } from "src/context/AuthContext";

// Define route configuration by user type and onboarding status
export interface RouteConfig {
  path: string;
  requiresAuth: boolean;
  allowedUserTypes?: UserType[];
  requiresOnboarding?: boolean;
  onboardingPath?: string;
  fallbackPath?: string;
}

// Central route configuration
export const ROUTES: Record<string, RouteConfig> = {
  // --- PUBLIC ROUTES ---
  HOME: {
    path: "/",
    requiresAuth: false,
  },
  LOGIN: {
    path: "/login",
    requiresAuth: false,
  },
  REGISTER: {
    path: "/register",
    requiresAuth: false,
  },
  FORGOT_PASSWORD: {
    path: "/forgot-password",
    requiresAuth: false,
  },
  FORGOT_PASSWORD_CONFIRMATION: {
    path: "/forgot-password-confirmation",
    requiresAuth: false,
  },
  RESET_PASSWORD: {
    path: "/reset-password",
    requiresAuth: false,
  },
  ARTICLES: {
    path: "/articles",
    requiresAuth: false,
  },
  INQUIRY: {
    path: "/inquiry",
    requiresAuth: false,
  },
  FEEDBACK: {
    path: "/feedback",
    requiresAuth: false,
  },
  BECOME_TUTOR: {
    path: "/become-tutor",
    requiresAuth: false,
  },
  CONFIRM_EMAIL: {
    path: "/confirm-email",
    requiresAuth: false,
  },
  TERMS: {
    path: "/terms",
    requiresAuth: false,
  },
  PRIVACY: {
    path: "/privacy",
    requiresAuth: false,
  },
  HOW_IT_WORKS: {
    path: "/how-it-works",
    requiresAuth: false,
  },
  RESOURCES: {
    path: "/resources",
    requiresAuth: false,
  },
  COURSE_MATHEMATICS: {
    path: "/courses/mathematics",
    requiresAuth: false,
  },
  COURSE_PYTHON: {
    path: "/courses/computer-science/python",
    requiresAuth: false,
  },
  COURSE_JAVASCRIPT: {
    path: "/courses/computer-science/javascript",
    requiresAuth: false,
  },
  ABOUT: {
    path: "/about",
    requiresAuth: false,
  },
  CAREERS: {
    path: "/careers",
    requiresAuth: false,
  },
  PRICING: {
    path: "/pricing",
    requiresAuth: false,
  },
  TUTORS: {
    path: "/tutors",
    requiresAuth: false,
  },
  CONTACT: {
    path: "/contact",
    requiresAuth: false,
  },
  BOOK_DEMO: {
    path: "/pages/tutor/bookdemo",
    requiresAuth: false,
  },
  AUTH_CALLBACK: {
    path: "/auth/callback",
    requiresAuth: false,
  },
  PHOTO_UPLOAD_TEST: {
    path: "/photo-upload-test",
    requiresAuth: false,
  },
  TEST: {
    path: "/test",
    requiresAuth: false,
  },
  MAINTENANCE: {
    path: "/maintenance",
    requiresAuth: false,
  },
  DIAGNOSTIC_ASSESSMENT: {
    path: "/diagnostic-assessment",
    requiresAuth: false,
  },
  DIAGNOSTIC_RUNNER: {
    path: "/diagnostic-assessment/:attemptId",
    requiresAuth: false,
  },
  DIAGNOSTIC_REPORT: {
    path: "/diagnostic-assessment/:attemptId/report",
    requiresAuth: false,
  },
  // --- END PUBLIC ROUTES ---
  STUDENT_AREA: {
    path: "/student",
  requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
  fallbackPath: "/",
  },
  TUTOR_AREA: {
  path: "/tutor",
  requiresAuth: true,
  allowedUserTypes: ["tutor"],
  requiresOnboarding: true,
  fallbackPath: "/",
},
  ADMIN_AREA: {
    path: "/admin",
  requiresAuth: true,
    allowedUserTypes: ["admin"],
  fallbackPath: "/",
},
  STUDENT_DASHBOARD: {
    path: "/student/dashboard",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    onboardingPath: "onboard-student",
    fallbackPath: "/",
  },
  TUTOR_DASHBOARD: {
    path: "/tutor/dashboard",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    onboardingPath: "/onboard-tutor",
    fallbackPath: "/",
  },
  ADMIN_DASHBOARD: {
    path: "/admin/dashboard",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/",
  },
  ADMIN_LIVE_VIEW: {
    path: "/admin/live-view",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  TUTOR_SCHEDULE: {
    path: "/tutor/schedule",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_PAST_SESSIONS: {
    path: "/tutor/sessions/history",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_STUDENT_OVERVIEW: {
    path: "/tutor/students/overview",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_STUDENT_PERFORMANCE: {
    path: "/tutor/students/performance",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_STUDENT_JOURNEY: {
    path: "/tutor/students/journey",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_NODE_FEEDBACK: {
    path: "/tutor/node-feedback",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  STUDENT_ONBOARDING: {
    path: "/onboard-student",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: false,
    fallbackPath: "/",
  },
  TUTOR_ONBOARDING: {
    path: "/onboard-tutor",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: false,
    fallbackPath: "/",
  },
  STUDENT_PROFILE: {
    path: "/student/profile",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/",
  },
  STUDENT_SCHEDULE: {
    path: "/student/schedule",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_JOURNEY: {
    path: "/student/journey",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_CURRICULUM_PROGRESS: {
    path: "/student/curriculum-progress",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_INSIGHTS: {
    path: "/student/insights",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_MATERIALS: {
    path: "/student/materials",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_REQUEST_BOOKING: {
    path: "/student/request-booking",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    //requiresEnrollment: true,
    fallbackPath: "/student/dashboard",
  },
    TUTOR_PROFILE: {
    path: "/tutor/profile",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/",
  },
  STUDENT_PRODUCTS: {
    path: "/student/products",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_BILLING_HISTORY: {
    path: "/student/billing-history",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_SUBSCRIPTIONS: {
    path: "/student/subscriptions",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_NEW_SUBSCRIPTION: {
    path: "/student/subscriptions/new",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_NEW_SUBSCRIPTION_SELECT: {
    path: "/student/subscriptions/new/select-package",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_NEW_SUBSCRIPTION_CONFIGURE: {
    path: "/student/subscriptions/new/configure",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_NEW_SUBSCRIPTION_PRICING: {
    path: "/student/subscriptions/new/pricing",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_NEW_SUBSCRIPTION_PURCHASE: {
    path: "/student/subscriptions/new/purchase",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_ACCOUNT_PREFERENCES: {
    path: "/student/account-preferences",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_NOTIFICATIONS: {
    path: "/student/notifications",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  JOIN_SESSION: {
    path: "/join-session/:sessionId",
    requiresAuth: true,
    allowedUserTypes: ["student", "tutor", "admin"],
    requiresOnboarding: true,
    fallbackPath: "/",
  },
  STUDENT_BOOK_SESSIONS: {
    path: "/student/book-sessions",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_UPCOMING_SESSIONS: {
    path: "/student/sessions/upcoming",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  STUDENT_PAST_SESSIONS: {
    path: "/student/sessions/history",
    requiresAuth: true,
    allowedUserTypes: ["student"],
    requiresOnboarding: true,
    fallbackPath: "/student/dashboard",
  },
  TUTOR_ACCOUNT_PREFERENCES: {
    path: "/tutor/account-preferences",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_NOTIFICATIONS: {
    path: "/tutor/notifications",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  ADMIN_BATCHES: {
    path: "/admin/batches",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_ENROLLMENTS: {
    path: "/admin/enrollments",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_VIEW_ENROLLMENTS: {
    path: "/admin/enrollments/view",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_CURRICULUM_CATALOG: {
    path: "/admin/curriculum",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_ENROLLMENTS_CREATE: {
    path: "/admin/enrollments/create",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_CREATE_BATCH: {
    path: "/admin/batches/create",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_VIEW_BATCHES: {
    path: "/admin/batches/view",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_EDIT_BATCH: {
    path: "/admin/batches/edit",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_EXTEND_BATCH: {
    path: "/admin/batches/:batchId/extend",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_DELETE_BATCH: {
    path: "/admin/batches/delete",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_INQUIRIES: {
    path: "/admin/inquiries",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_DEMO_BOOKINGS: {
    path: "/admin/DemoBookings",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_TUTORS: {
    path: "/admin/tutors",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_TUTORS_CREATE: {
    path: "/admin/tutors/create",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_TUTORS_LIST: {
    path: "/admin/tutors/list",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_TUTORS_VIEW: {
    path: "/admin/tutors/view/:tutorId?",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_TUTORS_EDIT: {
    path: "/admin/tutors/edit/:tutorId?",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_TUTORS_DELETE: {
    path: "/admin/tutors/delete/:tutorId?",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_TUTORS_APPROVAL_QUEUE: {
    path: "/admin/tutors/approval-queue",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_USERS: {
    path: "/admin/users",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_USERS_CREATE: {
    path: "/admin/users/create",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_USERS_LIST: {
    path: "/admin/users/list",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_USERS_VIEW: {
    path: "/admin/users/view",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_USERS_EDIT: {
    path: "/admin/users/edit",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_USERS_DELETE: {
    path: "/admin/users/delete",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS: {
    path: "/admin/sessions",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS_SCHEDULE: {
    path: "/admin/sessions/schedule",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS_EDIT: {
    path: "/admin/sessions/edit",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS_ADD_USERS: {
    path: "/admin/sessions/add-users",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS_REMOVE_USER: {
    path: "/admin/sessions/remove-user",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS_LIST: {
    path: "/admin/sessions/list",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS_ANALYTICS: {
    path: "/admin/sessions/analytics",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  ADMIN_SESSIONS_CONFIGURATION: {
    path: "/admin/sessions/configuration",
    requiresAuth: true,
    allowedUserTypes: ["admin"],
    fallbackPath: "/admin/dashboard",
  },
  TUTOR_PROFILE_RESOURCES: {
    path: "/tutor/profile/resources",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_ALL_SESSIONS: {
    path: "/tutor/sessions/all",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
  TUTOR_UPCOMING_SESSIONS: {
    path: "/tutor/sessions/upcoming",
    requiresAuth: true,
    allowedUserTypes: ["tutor"],
    requiresOnboarding: true,
    fallbackPath: "/tutor/dashboard",
  },
};

// Helper function to get the appropriate route based on user state
export const getRouteForUser = (
  userType: UserType,
  isOnboarded: boolean,
  userStatus: string | null = "registered",
  source: string | null = null
): string => {
  console.log("getRouteForUser called with:", {
    userType,
    isOnboarded,
    userStatus,
    source,
  });

  // Handle special case for tutor application flow
  if (source === "become-tutor") {
    // If user is a tutor with incomplete onboarding, send to tutor onboarding
    if (userStatus === "registered" && userType === "tutor" && !isOnboarded) {
      console.log("Routing to tutor onboarding (from become-tutor flow)");
      return ROUTES.TUTOR_ONBOARDING.path;
    }

    // If user is a guest, they need to complete signup
    if (userStatus === "guest") {
      console.log("Routing to register (guest in become-tutor flow)");
      return "/register";
    }

    // If user is new, they can proceed with the tutor application
    if (userStatus === "new") {
      console.log("Routing to become-tutor page (new user)");
      return ROUTES.BECOME_TUTOR.path;
    }
  }

  // Standard routing logic
  if (!userType) return ROUTES.LOGIN.path;

  if (userType === "admin") {
    console.log("Admin user, routing to admin dashboard");
    return ROUTES.ADMIN_DASHBOARD.path;
  }

  if (isOnboarded === false) {
    if (userType === "tutor") {
      console.log("Tutor needs onboarding, routing to tutor onboarding");
      return ROUTES.TUTOR_ONBOARDING.path;
    } else {
      console.log("Student needs onboarding, routing to student onboarding");
      return ROUTES.STUDENT_ONBOARDING.path;
    }
  }

  if (userType === "tutor") {
    console.log("Onboarded tutor, routing to tutor dashboard");
    return ROUTES.TUTOR_DASHBOARD.path;
  } else {
    console.log("Onboarded student, routing to student dashboard");
    return ROUTES.STUDENT_DASHBOARD.path;
  }
};

// Helper function to handle email confirmation redirects
export const getEmailConfirmationRedirect = (
  source: string | null,
  userType: UserType,
  isOnboarded: boolean
): string => {
  // For tutor application flow
  if (source === "become-tutor") {
    // If user is a tutor with incomplete onboarding, send to tutor onboarding
    if (userType === "tutor" && !isOnboarded) {
      return ROUTES.TUTOR_ONBOARDING.path;
    }
    // Otherwise send to become-tutor page
    return ROUTES.BECOME_TUTOR.path;
  }

  // For regular signup flow, send to login
  return ROUTES.LOGIN.path;
};

// Helper function to get post-registration redirect path
export const getPostRegistrationRedirect = (
  email: string,
  userType: string,
  isGuestUpgrade: boolean = false
): string => {
  // Always redirect to confirm-email page with appropriate state
  return ROUTES.CONFIRM_EMAIL.path;
};
